import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Building2, KeyRound, Home, Phone, MessageSquare, Clock, ShieldCheck, ArrowRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo, testimonials } from '../mockData';
import usePageMeta from '../hooks/usePageMeta';

// How far we've come — told as stages, not dates.
const journey = [
  {
    icon: Wrench,
    title: 'Where it started',
    text: 'A small install crew fitting door hardware for builders — turning up on time, doing it properly and leaving the site clean.',
  },
  {
    icon: Building2,
    title: 'Trusted by the trade',
    text: "Auckland's access control companies and builders started calling us when the hardware had to go in right. Hospitals, banks, schools, libraries and train stations followed.",
  },
  {
    icon: ShieldCheck,
    title: 'A specialty of our own',
    text: 'We became the only business in Auckland certified to install and service the AHS A200 door actuator — automatic door opening we regularly deliver for disabled customers through ACC.',
  },
  {
    icon: KeyRound,
    title: 'Open to everyone',
    text: 'Today we are a full-service locksmith for homeowners and businesses directly — lockouts, rekeying, key cutting, repairs and upgrades — alongside the commercial work we are known for.',
  },
];

// Service front and centre — what a customer actually gets.
const servicePromises = [
  { icon: Phone, title: 'A real person answers', text: 'Call us and you talk to the people doing the work, not a call centre.' },
  { icon: MessageSquare, title: 'Straight answers', text: "Clear prices before we start, and honest advice — even when the answer is \"you don't need a new lock\"." },
  { icon: Clock, title: 'We turn up', text: 'When we give you a time, we keep it. If something changes, you hear it from us first.' },
  { icon: Home, title: 'Treat it like our own', text: 'Tidy work, no mess left behind, and a 12-month workmanship guarantee on everything we do.' },
];

const About = () => {
  usePageMeta();

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 lg:py-24 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/projects/north-shore-hospital.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">About Shore Doors Locksmiths</h1>
            <p className="text-xl sm:text-2xl text-gray-300">
              The installer the professionals already use — now a full-service locksmith for everyone
            </p>
          </div>
        </div>
      </section>

      {/* The story */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Our story</h2>
          <p className="text-lg text-gray-700 mb-6">
            Shore Doors built its name the unglamorous way. For years we were the installation
            partner that Auckland's access control companies and builders called when the hardware
            had to go in right — the locks, maglocks, electric strikes, drop bolts and door hardware
            behind hospitals, banks, schools, libraries and train stations.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            That work taught us what people actually want from a tradesperson: turn up when you
            said you would, tell them the truth, do the job properly and leave the place tidy. It
            is how we won the trust of the trade, and it is the same standard we bring to every
            front door, office and shop we work on today.
          </p>
          <p className="text-lg text-gray-700">
            Now we are a full-service locksmith business, open to homeowners and businesses
            directly — fully mobile, covering the North Shore for residential work and greater
            Auckland for commercial.
          </p>
        </div>
      </section>

      {/* How far we've come */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How far we've come</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From a small install crew to the locksmith the professionals already trust.
            </p>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 list-none p-0 m-0">
            {journey.map((step, i) => (
              <li key={step.title} className="bg-white rounded-xl border border-gray-200 p-6 min-w-0">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-red-600 text-white font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <step.icon className="text-gray-400" size={24} aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Service first */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Service comes first</h2>
            <p className="text-lg text-gray-700">
              Anyone can fit a lock. What keeps customers coming back is how they are looked after
              from the first call to the last screw — so that is what we put at the centre of the
              business.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {servicePromises.map((p) => (
              <div key={p.title} className="flex gap-4 min-w-0">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                  <p.icon className="text-red-600" size={24} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{p.title}</h3>
                  <p className="text-gray-600">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One testimonial + CTA */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="text-gray-300 mx-auto mb-4" size={36} />
          <blockquote className="text-xl text-gray-700 italic leading-relaxed mb-6">
            "{testimonials[4].quote}"
          </blockquote>
          <p className="font-semibold text-gray-900">{testimonials[4].name}</p>
          <p className="text-gray-500 text-sm mb-8">{testimonials[4].title}, {testimonials[4].company}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${businessInfo.phoneTel}`}>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                <Phone className="mr-2" size={18} />
                Call {businessInfo.phone}
              </Button>
            </a>
            <Link to="/reviews">
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100">
                Read all reviews
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
